import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { getDisplayName } from '../../../../../models/NameUtils';
import { numberToCurrencyStringValue, numberToUSDCurrencyStringValue } from '../../../../../models/UiUtils';
import { useDonationFilterQuery, useDonationSummaryQuery } from '../../../../../queries';
import { styleSheet as dbStyleSheet } from '../../../../pages/DataBoards/Home/styles';
import { baseStyleSheet } from '../../../../styles/styles';
import { AddBoardItemButton } from '../../../boards/AddBoardItemButton';
import { Badge } from '../../../dataBoards/Badge';
import { EditDonationModal } from '../../../dataBoards/donations/EditDonationModal/';
import { dataGridStyles } from '../../../DataGrid/data-grid-styles';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { HeartIcon } from '../../../svgs/icons/HeartIcon';
import { ExpandableText } from '../../../../components-v2/ExpandableText';
import { styleSheet } from './styles';

type EntityDonationsMode = 'contact' | 'company';

export const EntityDonations = ({
	entity,
	mode = 'contact',
	householdsEnabled = false,
}: {
	entity: Api.IEntity;
	mode?: EntityDonationsMode;
	householdsEnabled?: boolean;
}) => {
	const [donationToEdit, setDonationToEdit] = React.useState<Api.IDonation | null>(null);
	const [refreshToken, setRefreshToken] = React.useState(new Date().getTime());
	const isCompanyMode = mode === 'company';

	// A joint gift is recorded against whichever spouse handed it over, so on a contact in a household the
	// board scopes to the household and names the giver on every row instead of implying the viewed contact.
	const contact = isCompanyMode ? null : (entity as Api.IContact);
	const householdId = householdsEnabled ? contact?.household?.id : undefined;
	const isHouseholdRollUp = !!householdId;

	const scopeCriteria: Api.IDonationFilterCriteria = isCompanyMode
		? { property: Api.DonationFilterProperty.CompanyId, value: entity.id }
		: isHouseholdRollUp
			? { property: Api.DonationFilterProperty.HouseholdId, value: householdId }
			: { property: Api.DonationFilterProperty.ContactId, value: entity.id };

	const filterRequest: Api.IDonationFilterRequest = { criteria: [scopeCriteria] };

	const donationFilterQuery = useDonationFilterQuery({
		enabled: !!entity.id,
		filterRequest,
		expand: 'contact,TagsCollection',
	});

	const summaryQuery = useDonationSummaryQuery({
		enabled: (isCompanyMode || isHouseholdRollUp) && !!entity.id,
		filterRequest,
		meta: {
			toastErrorMessage: isCompanyMode
				? 'Failed to load donation totals for this company'
				: 'Failed to load donation totals for this household',
			errorLogging: {
				category: 'EntityDonations',
				action: isCompanyMode ? 'CompanyDonationSummary-Error' : 'HouseholdDonationSummary-Error',
			},
		},
	});

	const donations = React.useMemo(() => {
		return donationFilterQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [donationFilterQuery.data]);

	const onFetchNextBatchOfDonations = () => {
		if (donationFilterQuery.hasNextPage && !donationFilterQuery.isFetchingNextPage) {
			donationFilterQuery.fetchNextPage();
		}
	};

	// A company is not a valid donor: the donation still has to land on one of its contacts, so the
	// company view opens the modal with no contact prefilled and the picker scoped to that company.
	const addItemClicked = () => {
		setDonationToEdit((isCompanyMode ? {} : { contact: entity }) as Api.IDonation);
	};

	const refreshDonations = () => {
		setRefreshToken(new Date().getTime());
		setDonationToEdit(null);
	};

	const summary = summaryQuery.data;
	const contactCount = summary?.contactCount ?? 0;
	const showRollUpSummary = isCompanyMode && !!summary;
	// Resolves only for a household of exactly two named spouses, so the generic wording has to hold up.
	const householdName = contact?.household?.name;
	const showHouseholdSummary = isHouseholdRollUp && !!summary && !!summary.totalDonations;
	const showDonorName = isCompanyMode || isHouseholdRollUp;
	const boardTitle = isHouseholdRollUp ? `Donations · ${householdName ?? 'Household'}` : 'Donations';

	if (donationFilterQuery.isPending) {
		return <LoadingSpinner />;
	}

	return (
		<div key={refreshToken}>
			<div className={css(styleSheet.contentSection)}>
				<header className={css(styleSheet.contentHeader)}>
					<div className={css(dbStyleSheet.boardIconContainer, baseStyleSheet.bgPink)}>
						<HeartIcon height={24} width={24} className={css(styleSheet.donationsIcon)} />
					</div>
					<h6 className={css(styleSheet.contentListTitle)}>{boardTitle}</h6>
					<AddBoardItemButton className={css(styleSheet.contentAddButton)} onClick={addItemClicked}>
						Add Donation
					</AddBoardItemButton>
				</header>

				{showRollUpSummary ? (
					// The roll-up only covers contacts linked to this company, and the contact set is
					// ACL-scoped to the viewer, so the total is described by what it actually spans.
					// A truncated fan-out gets no total at all rather than a misleadingly partial one.
					<p className={css(styleSheet.rollUpSummary)}>
						{summary.contactsTruncated
							? `Showing donations from the first ${contactCount} contacts at this company.`
							: `Total from ${contactCount} ${contactCount === 1 ? 'contact' : 'contacts'} at this company: ${numberToUSDCurrencyStringValue(summary.totalAmount ?? 0)}`}
					</p>
				) : null}

				{showHouseholdSummary ? (
					<p className={css(styleSheet.rollUpSummary)}>
						{`Household total: ${numberToUSDCurrencyStringValue(summary.totalAmount ?? 0)} across ${summary.totalDonations} ${
							summary.totalDonations === 1 ? 'gift' : 'gifts'
						}. Largest gift: ${numberToUSDCurrencyStringValue(summary.largestDonation ?? 0)}`}
					</p>
				) : null}

				{!donations.length && !showRollUpSummary && !showHouseholdSummary ? (
					<p className={css(styleSheet.emptyText)}>
						{isCompanyMode
							? 'No donations exist for this company.'
							: isHouseholdRollUp
								? 'No donations exist for this household.'
								: 'No donations exist for this contact.'}
					</p>
				) : null}

				{donations.length ? (
					<>
						<table className={css(dataGridStyles.table, styleSheet.tableOverrides)}>
							<tbody>
								{donations.map(donation => {
									const borderlessRow = donation === donations[0];

									const rowStyles = borderlessRow
										? dataGridStyles.tableDataCellBorderless
										: dataGridStyles.tableDataCell;

									const amountCell = (
										<td key='amount' className={css(rowStyles)}>
											<span className={css(styleSheet.amountText)}>
												${numberToCurrencyStringValue(donation.amount)}
											</span>
										</td>
									);

									const donorCell = (
										<td key='donor' className={css(rowStyles)}>
											<span className={css(styleSheet.donorName)}>
												{donation.contact ? getDisplayName(donation.contact) : donation.donorName}
											</span>
										</td>
									);

									const campaignCell = (
										<td key='campaign' className={css(rowStyles)}>
											{donation.campaign ? (
												<Badge text={donation.campaign.name} foregroundColor={donation.campaign.foregroundColor} />
											) : null}
										</td>
									);

									const dateCell = (
										<td key='date' className={css(rowStyles)}>
											<span>{moment(donation.date).format('MM/DD/YYYY')}</span>
										</td>
									);

									return (
										<React.Fragment key={`donation-${donation.id}`}>
											{/* Company and household mode mirror the donations board's column order; the
											    campaign badge takes the slot the board gives the contact chip. */}
											<tr
												aria-label={`Edit donation from ${
													donation.contact ? getDisplayName(donation.contact) : donation.donorName
												}`}
												className={css(styleSheet.clickableRow)}
												onClick={() => setDonationToEdit(donation)}
												onKeyDown={ev => {
													if (ev.key === 'Enter' || ev.key === ' ') {
														ev.preventDefault();
														setDonationToEdit(donation);
													}
												}}
												role='button'
												tabIndex={0}
											>
												{showDonorName
													? [donorCell, campaignCell, dateCell, amountCell]
													: [amountCell, campaignCell, dateCell]}
											</tr>
											{donation.details && (
												<tr>
													<td
														colSpan={showDonorName ? 4 : 3}
														className={css(dataGridStyles.tableDataCellBorderless, styleSheet.detailsCell)}
													>
														<ExpandableText text={donation.details} prefix='Details: ' />
													</td>
												</tr>
											)}
										</React.Fragment>
									);
								})}
							</tbody>
						</table>
						<Waypoint bottomOffset='-200px' onEnter={onFetchNextBatchOfDonations} />
					</>
				) : null}
			</div>

			{donationToEdit ? (
				<EditDonationModal
					initialDonation={donationToEdit}
					restrictToCompanyId={isCompanyMode ? entity.id : undefined}
					isOpen={!!donationToEdit}
					setIsOpen={open => !open && setDonationToEdit(null)}
					onSave={refreshDonations}
					onCancel={refreshDonations}
				/>
			) : null}
		</div>
	);
};
